import React, { useState, useMemo, useContext, useCallback, useEffect, useRef } from 'react';
import {
  DataGrid,
  GridToolbarContainer,
  GridToolbarColumnsButton,
  GridToolbarDensitySelector,
  GridToolbarQuickFilter,
  GridRow,
} from '@mui/x-data-grid';
import {
  Box,
  TextField,
  Chip,
  Tooltip,
  Popover,
  Stack,
  Button,
  Typography,
  Checkbox,
  Radio,
  InputAdornment,
  FormControlLabel,
  Switch,
  IconButton,
  Select,
  MenuItem,
  Divider,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { ThemeProvider } from '@mui/material/styles';
import { toast } from 'react-toastify';

import moment from 'moment';
import ScriptContext from '../../context/Script';
import { UserContext } from '../../context/User';
import SessionContext from '../../context/Session';
import dictionaryService from '../../services/dictionaryService';
import scriptService from '../../services/scriptService';
import shotService from '../../services/shotService';
import { MUItheme } from '../../styles/muiStyleUpdates';

// ─── Constants ────────────────────────────────────────────────────────────

const EDITABLE_FIELDS = new Set([
  'type',
  'cameraUnit',
  'camera',
  'movement',
  'frame',
  'focus',
  'focal',
  'subject',
  'shot_description',
  'notes',
]);

const FIELD_LABELS = {
  type: 'Type',
  cameraUnit: 'Camera Unit',
  camera: 'Camera',
  movement: 'Movement',
  frame: 'Frame',
  focus: 'Focus',
  focal: 'Focal',
  subject: 'Subjects',
  shot_description: 'Description',
  notes: 'Notes',
};

const FIELD_TO_API = {
  type: 'shot_type1',
  cameraUnit: 'selected_camera',
  camera: 'camera',
  movement: 'movement',
  frame: 'frame',
  focus: 'focus',
  focal: 'focal',
  subject: 'subject',
  shot_description: 'shot_description',
  notes: 'notes',
};

const ARRAY_FIELDS = new Set(['camera', 'movement', 'frame', 'focus', 'focal']);

const CAMERA_UNIT_VALUES = ['A', 'B', 'C', 'D'];

const PURPLE = '#7A64EB';

const RowDragContext = React.createContext(null);

// ─── Display helpers ──────────────────────────────────────────────────────

function toEighths(value) {
  if (value == null || value === '') return '';
  const n = parseFloat(value);
  if (isNaN(n) || n === 0) return '0';
  const eighths = Math.round(n * 8);
  const whole = Math.floor(eighths / 8);
  const remainder = eighths % 8;
  if (remainder === 0) return `${whole}`;
  if (whole === 0) return `${remainder}/8`;
  return `${whole} ${remainder}/8`;
}

function matchesFilterModel(row, filterModel) {
  const { items = [], logicOperator = 'and', quickFilterValues = [] } = filterModel;

  const activeItems = items.filter(
    ({ field, operator, value }) =>
      field &&
      operator &&
      ((value !== undefined && value !== '') ||
        operator === 'isEmpty' ||
        operator === 'isNotEmpty'),
  );

  if (activeItems.length > 0) {
    const results = activeItems.map(({ field, operator, value }) => {
      // subject is stored as [{subject_id}] objects — use pre-resolved names string
      const raw = field === 'subject' ? (row.subjectNames ?? '') : row[field];
      const cellStr = Array.isArray(raw) ? raw.join(' ') : String(raw ?? '');
      const cellLow = cellStr.toLowerCase();
      const valLow = String(value ?? '').toLowerCase();
      switch (operator) {
        case 'contains':
          return cellLow.includes(valLow);
        case 'notContains':
          return !cellLow.includes(valLow);
        case 'equals':
          return cellLow === valLow;
        case 'notEquals':
          return cellLow !== valLow;
        case 'startsWith':
          return cellLow.startsWith(valLow);
        case 'endsWith':
          return cellLow.endsWith(valLow);
        case 'isEmpty':
          return !cellStr.trim();
        case 'isNotEmpty':
          return !!cellStr.trim();
        default:
          return true;
      }
    });
    const passes = logicOperator === 'or' ? results.some(Boolean) : results.every(Boolean);
    if (!passes) return false;
  }

  if (quickFilterValues?.length > 0) {
    const searchText = [
      row.type,
      row.cameraUnit ? `Camera ${row.cameraUnit}` : '',
      row.shot_description,
      row.notes,
      String(row.shotNumber ?? ''),
      row.subjectNames,
      row.sceneHeader,
      String(row.sceneNumber ?? ''),
      ...(Array.isArray(row.camera) ? row.camera : []),
      ...(Array.isArray(row.movement) ? row.movement : []),
      ...(Array.isArray(row.frame) ? row.frame : []),
      ...(Array.isArray(row.focus) ? row.focus : []),
      ...(Array.isArray(row.focal) ? row.focal : []),
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();
    // Each space-separated term is an independent criterion — any match qualifies
    return quickFilterValues.some((word) => searchText.includes(word.toLowerCase()));
  }

  return true;
}

function getSortValue(row, field) {
  if (field === 'subject') return row.subjectNames ?? '';
  const v = row[field] ?? '';
  if (Array.isArray(v)) return v.join(', ');
  return v;
}

function sortShots(shots, sortModel) {
  if (!sortModel?.length) return shots;
  return [...shots].sort((a, b) => {
    for (const { field, sort } of sortModel) {
      const av = getSortValue(a, field);
      const bv = getSortValue(b, field);
      const cmp =
        typeof av === 'number' && typeof bv === 'number'
          ? av - bv
          : String(av).localeCompare(String(bv), undefined, { numeric: true });
      if (cmp !== 0) return sort === 'desc' ? -cmp : cmp;
    }
    return 0;
  });
}

function resolveLabel(storedValue, options) {
  const str = String(storedValue ?? '');
  if (!str || !Array.isArray(options)) return str;
  const found = options.find((o) => {
    if (typeof o === 'string') return o === str;
    return (
      String(o.value ?? o.id ?? o.key ?? '') === str || String(o.label ?? o.name ?? '') === str
    );
  });
  if (!found) return str;
  if (typeof found === 'string') return found;
  return String(found.label ?? found.name ?? found.value ?? str);
}

function normalizeOptions(options) {
  return (options || []).map((o) => {
    if (typeof o === 'string') return { value: o, label: o };
    const v = String(o.value ?? o.id ?? o.key ?? '');
    const l = String(o.label ?? o.name ?? v);
    return { value: v, label: l };
  });
}

function TruncatedCell({ value }) {
  if (!value) return null;
  return (
    <Tooltip title={value}>
      <Box
        sx={{
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        {value}
      </Box>
    </Tooltip>
  );
}

// ─── Inline picker (no floating dropdown) ─────────────────────────────────

function InlineMultiPicker({ options, selectedValues, onToggle }) {
  // options: [{value, label}], selectedValues: string[]
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return q ? options.filter((o) => o.label.toLowerCase().includes(q)) : options;
  }, [options, search]);

  return (
    <Box>
      {options.length > 5 && (
        <TextField
          size="small"
          placeholder="Search…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          autoFocus
          fullWidth
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                </InputAdornment>
              ),
            },
          }}
          sx={{ mb: 1 }}
        />
      )}

      <Box sx={{ maxHeight: 210, overflowY: 'auto' }}>
        {filtered.map((opt) => {
          const isSelected = selectedValues.some((v) => String(v) === String(opt.value));
          return (
            <Box
              key={opt.value}
              onClick={() => onToggle(opt.value)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 1,
                py: 0.5,
                borderRadius: 1,
                cursor: 'pointer',
                bgcolor: isSelected ? `${PURPLE}18` : 'transparent',
                '&:hover': { bgcolor: isSelected ? `${PURPLE}28` : 'action.hover' },
              }}
            >
              <Checkbox
                size="small"
                checked={isSelected}
                disableRipple
                sx={{ p: 0, '&.Mui-checked': { color: PURPLE } }}
              />
              <Typography variant="body2">{opt.label}</Typography>
            </Box>
          );
        })}
        {filtered.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ px: 1, py: 0.75 }}>
            No options found
          </Typography>
        )}
      </Box>

      {selectedValues.length > 0 && (
        <Box
          sx={{
            mt: 1.5,
            pt: 1.5,
            borderTop: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            flexWrap: 'wrap',
            gap: 0.5,
          }}
        >
          {selectedValues.map((v) => {
            const opt = options.find((o) => String(o.value) === String(v));
            return (
              <Chip
                key={v}
                size="small"
                label={opt?.label || v}
                onDelete={() => onToggle(v)}
                sx={{ bgcolor: `${PURPLE}18`, '& .MuiChip-deleteIcon': { color: PURPLE } }}
              />
            );
          })}
        </Box>
      )}
    </Box>
  );
}

function InlineSinglePicker({ options, selectedValue, onSelect }) {
  // options: string[], selectedValue: string
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return q ? options.filter((o) => o.toLowerCase().includes(q)) : options;
  }, [options, search]);

  return (
    <Box>
      {options.length > 5 && (
        <TextField
          size="small"
          placeholder="Search…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          autoFocus
          fullWidth
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                </InputAdornment>
              ),
            },
          }}
          sx={{ mb: 1 }}
        />
      )}

      <Box sx={{ maxHeight: 210, overflowY: 'auto' }}>
        {filtered.map((opt) => {
          const isSelected = opt === selectedValue;
          return (
            <Box
              key={opt}
              onClick={() => onSelect(isSelected ? '' : opt)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 1,
                py: 0.5,
                borderRadius: 1,
                cursor: 'pointer',
                bgcolor: isSelected ? `${PURPLE}18` : 'transparent',
                '&:hover': { bgcolor: isSelected ? `${PURPLE}28` : 'action.hover' },
              }}
            >
              <Radio
                size="small"
                checked={isSelected}
                disableRipple
                sx={{ p: 0, '&.Mui-checked': { color: PURPLE } }}
              />
              <Typography variant="body2">{opt}</Typography>
            </Box>
          );
        })}
        {filtered.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ px: 1, py: 0.75 }}>
            No options found
          </Typography>
        )}
      </Box>

      {selectedValue && (
        <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
          <Chip
            size="small"
            label={selectedValue}
            onDelete={() => onSelect('')}
            sx={{ bgcolor: `${PURPLE}18`, '& .MuiChip-deleteIcon': { color: PURPLE } }}
          />
        </Box>
      )}
    </Box>
  );
}

// ─── Cell components ──────────────────────────────────────────────────────

// Full-width scene group header — rendered via slots.row, not inside a column cell
const SceneGroupHeaderRow = React.memo(function SceneGroupHeaderRow({ row, style }) {
  return (
    <Box
      style={style}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        px: 2,
        py: 1,
        bgcolor: `${PURPLE}0D`,
        boxSizing: 'border-box',
      }}
    >
      <Box
        sx={{
          width: 28,
          height: 28,
          borderRadius: '50%',
          bgcolor: PURPLE,
          color: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 12,
          fontWeight: 700,
          flexShrink: 0,
        }}
      >
        {row.sceneNumber}
      </Box>
      {row.sceneIntExt && (
        <Typography component="span" sx={{ fontWeight: 700, fontSize: 13, color: '#222' }}>
          {row.sceneIntExt}
        </Typography>
      )}
      <Typography component="span" sx={{ fontWeight: 700, fontSize: 13, color: '#222' }}>
        {row.sceneHeader}
      </Typography>
      {row.sceneTimeOfDay && (
        <Typography component="span" sx={{ fontWeight: 700, fontSize: 13, color: '#222' }}>
          {row.sceneTimeOfDay}
        </Typography>
      )}
      {row.pageLength != null && row.pageLength !== '' && (
        <Typography
          component="span"
          sx={{ color: PURPLE, fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap' }}
        >
          {toEighths(row.pageLength)} PGS
        </Typography>
      )}
    </Box>
  );
});

const CustomGridRow = React.memo(function CustomGridRow(props) {
  const drag = useContext(RowDragContext);
  const rowId = props.row?.id;

  if (props.row?.isGroupHeader) {
    return <SceneGroupHeaderRow row={props.row} style={props.style} />;
  }

  const isDragging = drag?.draggingId === rowId;
  const isDragOver = drag?.dragOverId === rowId;

  return (
    <GridRow
      {...props}
      draggable={!!drag}
      style={{
        ...props.style,
        opacity: isDragging ? 0.3 : 1,
        boxShadow: isDragOver ? `inset 0 2px 0 0 ${drag?.canDrop ? PURPLE : '#E35640'}` : undefined,
        transition: 'opacity 0.12s',
      }}
      onDragStart={(e) => {
        e.dataTransfer.effectAllowed = 'move';
        drag?.onDragStart(props.row);
      }}
      onDragOver={(e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = drag?.canDrop ? 'move' : 'none';
        drag?.onDragOver(props.row);
      }}
      onDrop={(e) => {
        e.preventDefault();
        drag?.onDrop(props.row);
      }}
      onDragEnd={() => drag?.onDragEnd()}
    />
  );
});

function ShotNumberBadge({ label, circle }) {
  if (!label && label !== 0) return null;
  return (
    <Box sx={{ height: '100%', display: 'flex', alignItems: 'center' }}>
      <Box
        sx={{
          bgcolor: PURPLE,
          color: '#fff',
          borderRadius: circle ? '50%' : 1,
          width: circle ? 28 : undefined,
          height: circle ? 28 : undefined,
          minWidth: circle ? 28 : undefined,
          px: circle ? 0 : 1,
          py: circle ? 0 : 0.25,
          fontSize: 12,
          fontWeight: 700,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          lineHeight: 1.6,
          whiteSpace: 'nowrap',
        }}
      >
        {label}
      </Box>
    </Box>
  );
}

function ImageCell({ image, density }) {
  const isComfortable = density === 'comfortable';
  return (
    <Box
      sx={{
        height: '100%',
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Box
        sx={{
          width: isComfortable ? '98%' : 44,
          height: isComfortable ? '98%' : 44,
          border: '1px solid #E8E8E8',
          borderRadius: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
          bgcolor: '#F7F7F7',
          flexShrink: 0,
        }}
      >
        <img
          src={image || '/placeholderImg.png'}
          alt=""
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
      </Box>
    </Box>
  );
}

// ─── Custom toolbar with Group toggle + multi-filter ──────────────────────

const FILTER_FIELDS = [
  { value: 'type', label: 'Type', dict: 'types', operator: 'contains' },
  { value: 'subject', label: 'Subjects', dict: 'subjects', operator: 'contains' },
  { value: 'cameraUnit', label: 'Camera Unit', dict: 'cameraUnit', operator: 'equals' },
  { value: 'camera', label: 'Camera', dict: 'camera', operator: 'contains' },
  { value: 'movement', label: 'Movement', dict: 'movement', operator: 'contains' },
  { value: 'frame', label: 'Frame', dict: 'frame', operator: 'contains' },
  { value: 'focus', label: 'Focus', dict: 'focus', operator: 'contains' },
  { value: 'focal', label: 'Focal', dict: 'focal', operator: 'contains' },
  { value: 'shot_description', label: 'Description', dict: null, operator: 'contains' },
  { value: 'notes', label: 'Notes', dict: null, operator: 'contains' },
  { value: 'sceneNumber', label: 'Scene #', dict: null, operator: 'equals' },
  { value: 'sceneHeader', label: 'Scene Name', dict: null, operator: 'contains' },
];

const FILTER_OPERATORS = [
  { value: 'contains', label: 'contains' },
  { value: 'notContains', label: 'does not contain' },
  { value: 'equals', label: 'equals' },
  { value: 'notEquals', label: 'does not equal' },
  { value: 'startsWith', label: 'starts with' },
  { value: 'endsWith', label: 'ends with' },
  { value: 'isEmpty', label: 'is empty' },
  { value: 'isNotEmpty', label: 'is not empty' },
];

const NO_VALUE_OPERATORS = new Set(['isEmpty', 'isNotEmpty']);

function getFieldOptions(fieldDef, dictionaries) {
  if (!fieldDef?.dict) return [];
  if (fieldDef.dict === 'cameraUnit') {
    return CAMERA_UNIT_VALUES.map((v) => ({ value: v, label: `Camera ${v}` }));
  }
  const raw = dictionaries?.[fieldDef.dict] || [];
  if (fieldDef.dict === 'types') return raw.map((t) => ({ value: String(t), label: String(t) }));
  if (fieldDef.dict === 'subjects') {
    return raw
      .map((s) => {
        const label = String(s?.subject ?? s?.label ?? s?.name ?? s ?? '');
        return { value: label, label };
      })
      .filter((o) => o.value);
  }
  return normalizeOptions(raw);
}

function CustomTableToolbar({
  groupByScene,
  setGroupByScene,
  quickFilterProps,
  filterItems,
  onAddFilter,
  onRemoveFilter,
  onClearFilters,
  dictionaries,
}) {
  const [filterAnchor, setFilterAnchor] = useState(null);
  const [addField, setAddField] = useState('type');
  const [addOperator, setAddOperator] = useState('contains');
  const [addValue, setAddValue] = useState('');

  const activeFilters = useMemo(
    () =>
      (filterItems || []).filter(
        (f) => f.field && f.operator && (NO_VALUE_OPERATORS.has(f.operator) || (f.value != null && f.value !== '')),
      ),
    [filterItems],
  );

  const fieldDef = FILTER_FIELDS.find((f) => f.value === addField) || FILTER_FIELDS[0];
  const fieldOptions = useMemo(
    () => getFieldOptions(fieldDef, dictionaries),
    [fieldDef, dictionaries],
  );
  const isNoValueOperator = NO_VALUE_OPERATORS.has(addOperator);

  const handleAddFilter = () => {
    if (!addField || !addOperator) return;
    if (!isNoValueOperator && addValue === '') return;
    onAddFilter({
      id: Date.now(),
      field: addField,
      operator: addOperator,
      value: isNoValueOperator ? '' : String(addValue),
    });
    setAddValue('');
  };

  const handleRemoveFilter = (id) => onRemoveFilter(id);
  const handleClearAll = () => onClearFilters();

  return (
    <Box sx={{ borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
      <GridToolbarContainer sx={{ px: 1, py: 0.5, gap: 0.5, flexWrap: 'nowrap' }}>
        {/* Group by Scene — leftmost */}
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={groupByScene}
              onChange={(e) => setGroupByScene(e.target.checked)}
              sx={{
                '& .MuiSwitch-switchBase.Mui-checked': { color: PURPLE },
                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                  backgroundColor: PURPLE,
                },
              }}
            />
          }
          label={
            <Typography variant="body2" sx={{ fontSize: 12, color: '#555', whiteSpace: 'nowrap' }}>
              Group by Scene
            </Typography>
          }
          sx={{ ml: 0, mr: 0 }}
        />

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

        {/* Custom filter button */}
        <Button
          size="small"
          startIcon={<FilterListIcon sx={{ fontSize: 16 }} />}
          onClick={(e) => setFilterAnchor(e.currentTarget)}
          sx={{
            fontSize: 12,
            textTransform: 'none',
            color: activeFilters.length > 0 ? PURPLE : '#555',
            bgcolor: activeFilters.length > 0 ? `${PURPLE}14` : 'transparent',
            border: `1px solid ${activeFilters.length > 0 ? PURPLE : 'transparent'}`,
            '&:hover': { bgcolor: activeFilters.length > 0 ? `${PURPLE}22` : 'action.hover' },
            px: 1,
            py: 0.4,
          }}
        >
          Filters{activeFilters.length > 0 ? ` (${activeFilters.length})` : ''}
        </Button>

        <GridToolbarQuickFilter
          debounceMs={quickFilterProps?.debounceMs ?? 200}
          sx={{ ml: 1, flex: 1, maxWidth: 340 }}
          slotProps={{ input: { placeholder: 'Search shots…' } }}
        />

        <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 0.5, pr: 2 }}>
          <GridToolbarDensitySelector />
          <GridToolbarColumnsButton />
        </Box>
      </GridToolbarContainer>

      {/* Active filter chips */}
      {activeFilters.length > 0 && (
        <Box
          sx={{
            px: 1.5,
            pb: 0.75,
            display: 'flex',
            flexWrap: 'wrap',
            gap: 0.5,
            alignItems: 'center',
          }}
        >
          {activeFilters.map((f) => {
            const label = FILTER_FIELDS.find((ff) => ff.value === f.field)?.label || f.field;
            const operatorLabel =
              FILTER_OPERATORS.find((op) => op.value === f.operator)?.label || f.operator;
            const chipText = NO_VALUE_OPERATORS.has(f.operator)
              ? `${label} ${operatorLabel}`
              : `${label} ${operatorLabel} "${f.value}"`;
            return (
              <Chip
                key={f.id}
                size="small"
                label={chipText}
                onDelete={() => handleRemoveFilter(f.id)}
                sx={{
                  bgcolor: `${PURPLE}14`,
                  color: PURPLE,
                  fontSize: 11,
                  height: 24,
                  '& .MuiChip-deleteIcon': { color: PURPLE, fontSize: 14 },
                }}
              />
            );
          })}
          <Button
            size="small"
            onClick={handleClearAll}
            sx={{
              fontSize: 11,
              color: '#fff',
              bgcolor: PURPLE,
              '&:hover': { bgcolor: '#6a54d4' },
              textTransform: 'none',
              py: 0.25,
              minWidth: 0,
              px: 0.75,
              borderRadius: 1,
            }}
          >
            Clear all
          </Button>
        </Box>
      )}

      {/* Add filter popover */}
      <Popover
        open={Boolean(filterAnchor)}
        anchorEl={filterAnchor}
        onClose={() => setFilterAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            sx: { p: 2, minWidth: 260, borderRadius: 2, boxShadow: '0 4px 20px rgba(0,0,0,0.12)' },
          },
        }}
      >
        <Typography
          variant="caption"
          sx={{
            fontWeight: 700,
            color: PURPLE,
            letterSpacing: '0.07em',
            textTransform: 'uppercase',
            display: 'block',
            mb: 1.5,
          }}
        >
          Add Filter
        </Typography>
        <Stack spacing={1.5}>
          <Select
            size="small"
            value={addField}
            onChange={(e) => {
              const nextField = e.target.value;
              setAddField(nextField);
              const nextDef = FILTER_FIELDS.find((f) => f.value === nextField);
              setAddOperator(nextDef?.operator ?? 'contains');
              setAddValue('');
            }}
            fullWidth
          >
            {FILTER_FIELDS.map((f) => (
              <MenuItem key={f.value} value={f.value}>
                {f.label}
              </MenuItem>
            ))}
          </Select>

          <Select
            size="small"
            value={addOperator}
            onChange={(e) => setAddOperator(e.target.value)}
            fullWidth
          >
            {FILTER_OPERATORS.map((op) => (
              <MenuItem key={op.value} value={op.value}>
                {op.label}
              </MenuItem>
            ))}
          </Select>

          {isNoValueOperator ? null : fieldOptions.length > 0 ? (
            <Select
              size="small"
              value={addValue}
              onChange={(e) => setAddValue(e.target.value)}
              fullWidth
              displayEmpty
            >
              <MenuItem value="">
                <em>Select a value…</em>
              </MenuItem>
              {fieldOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          ) : (
            <TextField
              size="small"
              value={addValue}
              onChange={(e) => setAddValue(e.target.value)}
              placeholder={`Filter by ${fieldDef?.label || addField}…`}
              fullWidth
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleAddFilter();
                }
              }}
            />
          )}

          <Button
            size="small"
            variant="contained"
            disabled={!isNoValueOperator && addValue === ''}
            onClick={handleAddFilter}
            fullWidth
            sx={{ bgcolor: PURPLE, '&:hover': { bgcolor: '#6a54d4' }, textTransform: 'none' }}
          >
            Add Filter
          </Button>
        </Stack>
      </Popover>
    </Box>
  );
}

// ─── Edit Popover ─────────────────────────────────────────────────────────

const EditPopover = React.memo(function EditPopover({
  open,
  anchorEl,
  field,
  row,
  dictionaries,
  onSave,
  onCancel,
}) {
  const [value, setValue] = useState(null);
  const wasOpen = useRef(false);

  useEffect(() => {
    if (open && !wasOpen.current && row) {
      let fieldValue = row[field] ?? null;
      // Normalize stored numeric IDs to strings to match normalizeOptions() output
      if (ARRAY_FIELDS.has(field) && Array.isArray(fieldValue)) {
        fieldValue = fieldValue.map((v) => String(v));
      }
      setValue(fieldValue);
      wasOpen.current = true;
    }
    if (!open) wasOpen.current = false;
  }, [open, row, field]);

  const handleSave = useCallback(() => onSave(field, value, row), [onSave, field, value, row]);

  // Clicking outside saves; Escape cancels
  const handleClose = useCallback(
    (_, reason) => {
      if (reason === 'escapeKeyDown') onCancel();
      else handleSave();
    },
    [handleSave, onCancel],
  );

  const renderEditor = () => {
    if (!row || !field) return null;

    // ── Multi-select array fields ──
    if (ARRAY_FIELDS.has(field)) {
      const options = normalizeOptions(dictionaries[field]);
      const selected = Array.isArray(value) ? value : [];
      return (
        <InlineMultiPicker
          options={options}
          selectedValues={selected}
          onToggle={(v) =>
            setValue((prev) => {
              const cur = Array.isArray(prev) ? prev : [];
              return cur.some((x) => String(x) === String(v))
                ? cur.filter((x) => String(x) !== String(v))
                : [...cur, v];
            })
          }
        />
      );
    }

    // ── Shot type single-select ──
    if (field === 'type') {
      return (
        <InlineSinglePicker
          options={dictionaries.types || []}
          selectedValue={value || ''}
          onSelect={setValue}
        />
      );
    }

    // ── Camera Unit single-select ──
    if (field === 'cameraUnit') {
      return (
        <InlineSinglePicker
          options={CAMERA_UNIT_VALUES.map((v) => `Camera ${v}`)}
          selectedValue={value ? `Camera ${value}` : ''}
          onSelect={(label) => setValue(label ? label.replace('Camera ', '') : '')}
        />
      );
    }

    // ── Subjects multi-select ──
    if (field === 'subject') {
      const subjectOptions = (dictionaries.subjects || []).map((o) => ({
        value: String(o.id ?? o.subject_id ?? o.value ?? ''),
        label: String(o.subject ?? o.label ?? o.name ?? ''),
      }));
      // value = [{subject_id: 'uuid'}, ...]
      const selectedIds = (Array.isArray(value) ? value : []).map((s) => String(s.subject_id ?? s));
      return (
        <InlineMultiPicker
          options={subjectOptions}
          selectedValues={selectedIds}
          onToggle={(sid) => {
            setValue((prev) => {
              const cur = Array.isArray(prev) ? prev : [];
              const ids = cur.map((s) => String(s.subject_id ?? s));
              return ids.includes(sid)
                ? cur.filter((s) => String(s.subject_id ?? s) !== sid)
                : [...cur, { subject_id: sid }];
            });
          }}
        />
      );
    }

    // ── Description / Notes ──
    if (field === 'shot_description' || field === 'notes') {
      return (
        <TextField
          fullWidth
          multiline
          minRows={3}
          maxRows={8}
          label={FIELD_LABELS[field]}
          value={value || ''}
          onChange={(e) => setValue(e.target.value)}
          size="small"
          autoFocus
          InputLabelProps={{ shrink: true }}
          inputProps={{ maxLength: 2000 }}
        />
      );
    }

    return null;
  };

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={handleClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      disableRestoreFocus
      PaperProps={{
        sx: {
          p: 2,
          minWidth: 300,
          maxWidth: 400,
          borderRadius: 2,
          boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
        },
      }}
    >
      <Stack spacing={1.5}>
        <Typography
          variant="caption"
          sx={{
            fontWeight: 700,
            color: PURPLE,
            letterSpacing: '0.07em',
            textTransform: 'uppercase',
          }}
        >
          {FIELD_LABELS[field] || field}
        </Typography>

        <Box>{renderEditor()}</Box>

        <Stack direction="row" spacing={1} justifyContent="flex-end">
          <Button
            size="small"
            onClick={onCancel}
            sx={{
              color: '#666',
              border: '1px solid #ccc',
              bgcolor: 'transparent',
              '&:hover': { bgcolor: 'rgba(0,0,0,0.04)', borderColor: '#999' },
            }}
          >
            Cancel
          </Button>
          <Button size="small" onClick={handleSave}>
            Save
          </Button>
        </Stack>
      </Stack>
    </Popover>
  );
});

// ─── Main component ───────────────────────────────────────────────────────

const ShotListTable = () => {
  const { script, setScript } = useContext(ScriptContext);
  const { user } = useContext(UserContext);
  const { setGeneralModalDetails } = useContext(SessionContext);
  const isReadOnly = script.level === 'viewer';

  const [dictionaries, setDictionaries] = useState({
    frame: [],
    focus: [],
    camera: [],
    movement: [],
    focal: [],
    subjects: [],
    types: [],
  });
  const [dictsLoaded, setDictsLoaded] = useState(false);
  const dictionaryRef = useRef(dictionaries);

  const [popover, setPopover] = useState({ open: false, anchorEl: null, field: null, row: null });
  const [groupByScene, setGroupByScene] = useState(() => {
    try {
      return JSON.parse(sessionStorage.getItem(`sk_table_${script.id}_group`) || 'false');
    } catch {
      return false;
    }
  });
  const [filterItems, setFilterItems] = useState(() => {
    try {
      return JSON.parse(sessionStorage.getItem(`sk_table_${script.id}_filters`) || '[]');
    } catch {
      return [];
    }
  });
  const [quickFilterValues, setQuickFilterValues] = useState(() => {
    try {
      return JSON.parse(sessionStorage.getItem(`sk_table_${script.id}_qf`) || '[]');
    } catch {
      return [];
    }
  });
  const [sortModel, setSortModel] = useState(() => {
    try {
      return JSON.parse(sessionStorage.getItem(`sk_table_${script.id}_sort`) || '[]');
    } catch {
      return [];
    }
  });
  const [density, setDensity] = useState(() => {
    try {
      return sessionStorage.getItem(`sk_table_${script.id}_density`) || 'standard';
    } catch {
      return 'standard';
    }
  });
  const [columnVisibilityModel, setColumnVisibilityModel] = useState({
    image: false,
    camera: false,
    movement: false,
    frame: false,
    focus: false,
    focal: false,
  });
  const [imagesMap, setImagesMap] = useState({});

  // ── Drag-and-drop state ──
  const dragSrcRef = useRef(null);
  const dragOverPrevRef = useRef(null);
  const [draggingId, setDraggingId] = useState(null);
  const [dragOverId, setDragOverId] = useState(null);
  const [canDrop, setCanDrop] = useState(false);

  // ── Custom filter item handlers (functional updaters avoid stale closure issues) ──
  const handleAddFilterItem = useCallback((item) => {
    setFilterItems((prev) => [...prev, item]);
  }, []);
  const handleRemoveFilterItem = useCallback((id) => {
    setFilterItems((prev) => prev.filter((f) => f.id !== id));
  }, []);
  const handleClearFilterItems = useCallback(() => {
    setFilterItems([]);
  }, []);

  // Combines our chip filters + quick filter into one object for matchesFilterModel
  const effectiveFilterModel = useMemo(
    () => ({ items: filterItems, logicOperator: 'and', quickFilterValues }),
    [filterItems, quickFilterValues],
  );

  useEffect(() => {
    if (!columnVisibilityModel.image) return;
    let cancelled = false;
    (async () => {
      const updates = {};
      for (const scene of script.scenes || []) {
        for (const shot of scene.shot_list || []) {
          if (cancelled) return;
          if (shot.attachments?.length > 0) {
            const url = await shotService.retrieveImg(shot.id);
            if (url) updates[shot.id] = url;
          }
        }
      }
      if (!cancelled) setImagesMap((prev) => ({ ...prev, ...updates }));
    })();
    return () => {
      cancelled = true;
    };
  }, [columnVisibilityModel.image]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const load = async () => {
      const [frame, focus, camera, movement, focal, subjects, typeOptions] = await Promise.all([
        dictionaryService.fetchUserDictionary('frame', script.user_id),
        dictionaryService.fetchUserDictionary('focus', script.user_id),
        dictionaryService.fetchUserDictionary('camera', script.user_id),
        dictionaryService.fetchUserDictionary('movement', script.user_id),
        dictionaryService.fetchUserDictionary('focal', script.user_id),
        dictionaryService.fetchScriptDictionary('subjects', script.id),
        dictionaryService.fetchUserDictionary('shottype', user.id),
      ]);
      const loaded = {
        frame,
        focus,
        camera,
        movement,
        focal,
        subjects,
        types: (typeOptions || []).map((t) => t.label ?? t.value ?? t),
      };
      setDictionaries(loaded);
      dictionaryRef.current = loaded;
      setDictsLoaded(true);
    };
    load();
  }, [script.id, script.user_id, user.id]);

  useEffect(() => {
    sessionStorage.setItem(`sk_table_${script.id}_group`, JSON.stringify(groupByScene));
  }, [groupByScene, script.id]);
  useEffect(() => {
    sessionStorage.setItem(`sk_table_${script.id}_filters`, JSON.stringify(filterItems));
  }, [filterItems, script.id]);
  useEffect(() => {
    sessionStorage.setItem(`sk_table_${script.id}_qf`, JSON.stringify(quickFilterValues));
  }, [quickFilterValues, script.id]);
  useEffect(() => {
    sessionStorage.setItem(`sk_table_${script.id}_sort`, JSON.stringify(sortModel));
  }, [sortModel, script.id]);
  useEffect(() => {
    sessionStorage.setItem(`sk_table_${script.id}_density`, density);
  }, [density, script.id]);

  const resolveSubjectName = useCallback((subjectId) => {
    const sid = String(subjectId ?? '');
    // While the subject dictionary is still loading, don't fall back to the
    // raw id -- that's what causes the id to flash in the grid before the
    // dictionary resolves. Once loaded, an unmatched id still falls back to
    // showing the raw value (e.g. a stale/deleted subject).
    if (!dictsLoaded) return '';
    const subjects = dictionaries.subjects || [];
    const found = subjects.find((s) => String(s.id ?? s.subject_id ?? s.value ?? '') === sid);
    if (!found) return sid;
    return String(found.subject ?? found.label ?? found.name ?? sid);
  }, [dictionaries.subjects, dictsLoaded]);

  const rows = useMemo(() => {
    if (!script?.scenes) return [];
    const result = [];
    const ungroupedShots = [];

    for (const scene of script.scenes) {
      const pageLength =
        scene.scene_length ??
        (scene.page_end != null && scene.page_start != null
          ? Number((scene.page_end - scene.page_start).toFixed(2)) || null
          : null);

      const sceneShots = (scene.shot_list || []).map((shot) => ({
        id: shot.id,
        sceneId: scene.id,
        isGroupHeader: false,
        sceneNumber: scene.scene_number || '',
        sceneIntExt: scene.scene_int_ext || '',
        sceneHeader: scene.header_text || '',
        sceneTimeOfDay: scene.scene_time_of_day || '',
        pageLength,
        shotNumber: shot.shot_number ?? 0,
        image: imagesMap[shot.id] || shot.image || null,
        type: shot.shot_type1 || '',
        cameraUnit: shot.selected_camera || '',
        camera: shot.camera || [],
        movement: shot.movement || [],
        frame: shot.frame || [],
        focus: shot.focus || [],
        focal: shot.focal || [],
        subject: shot.subject || [],
        subjectNames: (shot.subject || [])
          .map((s) => resolveSubjectName(s.subject_id ?? s))
          .join(', '),
        shot_description: shot.shot_description || '',
        notes: shot.notes || '',
      }));

      if (groupByScene) {
        const matching = sceneShots.filter((shot) =>
          matchesFilterModel(shot, effectiveFilterModel),
        );
        if (matching.length === 0) continue;
        const sorted = sortShots(matching, sortModel);
        result.push({
          id: `group-${scene.id}`,
          isGroupHeader: true,
          sceneNumber: scene.scene_number || '',
          sceneIntExt: scene.scene_int_ext || '',
          sceneHeader: scene.header_text || '',
          sceneTimeOfDay: scene.scene_time_of_day || '',
          pageLength,
          sceneId: scene.id,
          shotNumber: null,
          type: '',
          cameraUnit: '',
          camera: [],
          movement: [],
          frame: [],
          focus: [],
          focal: [],
          subject: [],
          subjectNames: '',
          shot_description: '',
          notes: '',
        });
        result.push(...sorted);
      } else {
        ungroupedShots.push(...sceneShots);
      }
    }

    if (!groupByScene) {
      const matching = ungroupedShots.filter((shot) =>
        matchesFilterModel(shot, effectiveFilterModel),
      );
      result.push(...sortShots(matching, sortModel));
    }

    return result;
  }, [script.scenes, groupByScene, effectiveFilterModel, sortModel, imagesMap, resolveSubjectName]);

  const handleCellClick = useCallback(
    (params, event) => {
      if (isReadOnly) return;
      if (params.row.isGroupHeader) return;
      if (!EDITABLE_FIELDS.has(params.field)) return;
      const cellEl = event.target.closest('.MuiDataGrid-cell') || event.currentTarget;
      setPopover({ open: true, anchorEl: cellEl, field: params.field, row: params.row });
    },
    [isReadOnly],
  );

  const handlePopoverSave = useCallback(
    async (field, newValue, row) => {
      setPopover({ open: false, anchorEl: null, field: null, row: null });
      const apiField = FIELD_TO_API[field];
      const apiValue =
        field === 'subject' ? (newValue || []).map((s) => s.subject_id ?? s) : newValue;
      try {
        await scriptService.changeShotChange(row.id, apiField, apiValue, row.sceneId);
        setScript((prev) => {
          const updatedScenes = prev.scenes.map((scene) => {
            if (scene.id !== row.sceneId) return scene;
            const updatedShotList = (scene.shot_list || []).map((shot) => {
              if (shot.id !== row.id) return shot;
              return { ...shot, [apiField]: newValue };
            });
            return { ...scene, shot_list: updatedShotList };
          });
          return { ...prev, scenes: updatedScenes };
        });
        toast.success('Saved', { autoClose: 1000, hideProgressBar: true });
      } catch (err) {
        console.error('Error saving shot field:', err);
        toast.error('Failed to save change. Please try again.');
      }
    },
    [setScript],
  );

  const handlePopoverCancel = useCallback(() => {
    setPopover({ open: false, anchorEl: null, field: null, row: null });
  }, []);

  const handleAddShot = useCallback(
    async (row) => {
      try {
        const sceneIndex = script.scenes.findIndex((s) => s.id === row.sceneId);
        if (sceneIndex === -1) return;
        const scene = script.scenes[sceneIndex];
        const newShot = await shotService.addShot(scene.id, row.shotNumber + 1);
        if (!newShot) return;

        const shotIndex = scene.shot_list.findIndex((s) => s.id === row.id);
        const updated = [...scene.shot_list];
        updated.splice(shotIndex + 1, 0, newShot);
        const renumbered = updated.map((s, i) => ({ ...s, shot_number: i + 1 }));

        for (let i = 0; i < renumbered.length; i++) {
          if (updated[i].shot_number !== renumbered[i].shot_number) {
            await scriptService.changeShotChange(
              renumbered[i].id,
              'shot_number',
              renumbered[i].shot_number,
              scene.id,
            );
          }
        }
        setScript((prev) => {
          const scenes = [...prev.scenes];
          scenes[sceneIndex] = { ...scene, shot_list: renumbered };
          return { ...prev, scenes };
        });
        toast.success('Shot added', { autoClose: 1000, hideProgressBar: true });
      } catch (err) {
        console.error('Error adding shot:', err);
        toast.error('Failed to add shot. Please try again.');
      }
    },
    [script, setScript],
  );

  const handleDeleteShot = useCallback(
    (row) => {
      const doDelete = async () => {
        setGeneralModalDetails(false);
        try {
          const sceneIndex = script.scenes.findIndex((s) => s.id === row.sceneId);
          if (sceneIndex === -1) return;
          const scene = script.scenes[sceneIndex];

          // row.id 0 is Utils.buildTempShot's placeholder row (a scene with no real
          // shots yet) -- never persisted, so there's nothing to delete server-side.
          if (row.id !== 0) {
            await scriptService.changeShotChange(
              row.id,
              'deleted_at',
              moment().format('YYYY-MM-DD HH:mm:ss'),
              row.sceneId,
            );
          }

          const remaining = scene.shot_list.filter((s) => s.id !== row.id);
          const renumbered = remaining.map((s, i) => ({ ...s, shot_number: i + 1 }));
          const shifted = renumbered
            .filter((s, i) => remaining[i].shot_number !== s.shot_number)
            .map((s) => ({ shotId: s.id, shotNumber: s.shot_number }));
          if (shifted.length > 0) {
            await scriptService.renumberShots(scene.id, shifted);
          }
          setScript((prev) => {
            const scenes = [...prev.scenes];
            scenes[sceneIndex] = { ...scene, shot_list: renumbered };
            return { ...prev, scenes };
          });
          toast.success('Shot deleted.', { autoClose: 1500, hideProgressBar: true });
        } catch (err) {
          toast.error('Failed to delete shot. Please try again.');
        }
      };

      setGeneralModalDetails({
        open: true,
        header: <span>Delete Shot</span>,
        message: (
          <div>
            Are you sure you want to delete this shot? This action cannot be undone.
            <br />
            <br />
            <div className="fullWidthContainer">
              <button
                className="buttonGray OneHalfFlex"
                onClick={() => setGeneralModalDetails(false)}
              >
                Cancel
              </button>
              <button
                className="OneHalfFlex"
                style={{ backgroundColor: '#E35640', color: '#fff' }}
                onClick={doDelete}
              >
                Delete Shot
              </button>
            </div>
          </div>
        ),
      });
    },
    [script, setScript, setGeneralModalDetails],
  );

  // ── Drag handlers ──

  const handleDragStart = useCallback((row) => {
    dragSrcRef.current = row;
    setDraggingId(row.id);
  }, []);

  const handleDragOver = useCallback((row) => {
    if (dragOverPrevRef.current === row.id) return;
    dragOverPrevRef.current = row.id;
    const valid =
      !!dragSrcRef.current &&
      dragSrcRef.current.sceneId === row.sceneId &&
      dragSrcRef.current.id !== row.id;
    setDragOverId(row.id);
    setCanDrop(valid);
  }, []);

  const handleDragEnd = useCallback(() => {
    dragSrcRef.current = null;
    dragOverPrevRef.current = null;
    setDraggingId(null);
    setDragOverId(null);
    setCanDrop(false);
  }, []);

  const handleDrop = useCallback(
    async (tgtRow) => {
      const srcRow = dragSrcRef.current;
      // Always reset visual state immediately
      dragSrcRef.current = null;
      dragOverPrevRef.current = null;
      setDraggingId(null);
      setDragOverId(null);
      setCanDrop(false);

      if (!srcRow || srcRow.id === tgtRow.id || srcRow.sceneId !== tgtRow.sceneId) return;

      const scene = script.scenes.find((s) => s.id === srcRow.sceneId);
      if (!scene?.shot_list) return;

      const list = [...scene.shot_list];
      const srcIdx = list.findIndex((s) => s.id === srcRow.id);
      const tgtIdx = list.findIndex((s) => s.id === tgtRow.id);
      if (srcIdx === -1 || tgtIdx === -1) return;

      const [moved] = list.splice(srcIdx, 1);
      list.splice(tgtIdx > srcIdx ? tgtIdx - 1 : tgtIdx, 0, moved);

      const originalNums = Object.fromEntries(scene.shot_list.map((s) => [s.id, s.shot_number]));
      const renumbered = list.map((s, i) => ({ ...s, shot_number: i + 1 }));

      setScript((prev) => ({
        ...prev,
        scenes: prev.scenes.map((sc) =>
          sc.id === srcRow.sceneId ? { ...sc, shot_list: renumbered } : sc,
        ),
      }));

      try {
        const shifted = renumbered
          .filter((shot) => originalNums[shot.id] !== shot.shot_number)
          .map((shot) => ({ shotId: shot.id, shotNumber: shot.shot_number }));
        if (shifted.length > 0) {
          await scriptService.renumberShots(srcRow.sceneId, shifted);
        }
      } catch (err) {
        console.error('Error saving shot order:', err);
        toast.error('Failed to save order. Please try again.');
      }
    },
    [script.scenes, setScript],
  );

  const dragCtx = useMemo(
    () => ({
      draggingId,
      dragOverId,
      canDrop,
      onDragStart: handleDragStart,
      onDragOver: handleDragOver,
      onDrop: handleDrop,
      onDragEnd: handleDragEnd,
    }),
    [draggingId, dragOverId, canDrop, handleDragStart, handleDragOver, handleDrop, handleDragEnd],
  );

  const subjectsValueGetter = useCallback(
    (value) => {
      if (!Array.isArray(value)) return '';
      return value.map((s) => resolveSubjectName(s.subject_id ?? s)).join(', ');
    },
    [resolveSubjectName],
  );

  const columns = useMemo(() => {
    const makeArrayCol = (field, headerName, options, width = 130) => ({
      field,
      headerName,
      width,
      minWidth: 80,
      sortable: true,
      editable: false,
      valueGetter: (value) =>
        Array.isArray(value) ? value.map((v) => resolveLabel(v, options)).join(', ') : value || '',
      renderCell: (params) => <TruncatedCell value={params.value} />,
    });

    const dragCol = !isReadOnly
      ? {
          field: '__drag__',
          headerName: '',
          width: 28,
          minWidth: 28,
          sortable: false,
          filterable: false,
          disableColumnMenu: true,
          hideable: false,
          editable: false,
          renderCell: (params) => {
            if (params.row?.isGroupHeader) return null;
            return (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '100%',
                  color: '#CCCCCC',
                  '&:hover': { color: '#888' },
                }}
              >
                <DragIndicatorIcon sx={{ fontSize: 16 }} />
              </Box>
            );
          },
        }
      : null;

    const actionCol = !isReadOnly
      ? {
          field: 'actions',
          headerName: '',
          width: 72,
          minWidth: 72,
          sortable: false,
          filterable: false,
          disableColumnMenu: true,
          hideable: false,
          editable: false,
          renderCell: (params) => {
            if (params.row?.isGroupHeader) return null;
            return (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, height: '100%' }}>
                <Tooltip title="Add shot below" placement="top">
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleAddShot(params.row);
                    }}
                    sx={{ color: '#BBBBBB', '&:hover': { color: PURPLE } }}
                  >
                    <AddIcon sx={{ fontSize: 17 }} />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Delete shot" placement="top">
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteShot(params.row);
                    }}
                    sx={{ color: '#BBBBBB', '&:hover': { color: '#E35640' } }}
                  >
                    <DeleteOutlineIcon sx={{ fontSize: 17 }} />
                  </IconButton>
                </Tooltip>
              </Box>
            );
          },
        }
      : null;

    const shotCols = [
      {
        field: 'shotNumber',
        headerName: 'Shot #',
        width: 80,
        minWidth: 60,
        sortable: !groupByScene,
        editable: false,
        align: 'left',
        headerAlign: 'left',
        valueGetter: (value) => (value != null ? value : ''),
        renderCell: (params) => {
          if (params.row?.isGroupHeader) return null;
          const scene = params.row?.sceneNumber ?? '';
          const shot = params.value ?? '';
          const label = scene !== '' ? `${scene}.${shot}` : String(shot);
          return <ShotNumberBadge label={label} />;
        },
      },
      {
        field: 'image',
        headerName: 'Image',
        width: 68,
        minWidth: 60,
        sortable: false,
        editable: false,
        renderCell: (params) =>
          params.row?.isGroupHeader ? null : (
            <ImageCell image={params.row?.image} density={density} />
          ),
      },
      {
        field: 'type',
        headerName: 'Type',
        width: 140,
        minWidth: 80,
        sortable: true,
        editable: false,
      },
      {
        field: 'cameraUnit',
        headerName: 'Camera Unit',
        width: 120,
        minWidth: 90,
        sortable: true,
        editable: false,
        valueGetter: (value) => (value ? `Camera ${value}` : ''),
        renderCell: (params) => <TruncatedCell value={params.value} />,
      },
      makeArrayCol('camera', 'Camera', dictionaries.camera),
      makeArrayCol('movement', 'Movement', dictionaries.movement),
      makeArrayCol('frame', 'Frame', dictionaries.frame),
      makeArrayCol('focus', 'Focus', dictionaries.focus),
      makeArrayCol('focal', 'Focal', dictionaries.focal),
      {
        field: 'subject',
        headerName: 'Subjects',
        width: 200,
        minWidth: 100,
        sortable: true,
        editable: false,
        valueGetter: subjectsValueGetter,
        renderCell: (params) => {
          // Don't paint anything until the subject dictionary has loaded --
          // avoids a flash of unresolved subject ids while it's in flight.
          if (!dictsLoaded) return null;
          const subjects = params.row?.subject;
          if (!Array.isArray(subjects) || subjects.length === 0) return null;
          return (
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 0.5,
                alignItems: 'center',
                height: '100%',
                py: 0.25,
              }}
            >
              {subjects.map((s, i) => {
                const sid = s?.subject_id ?? s;
                const name = resolveSubjectName(sid);
                return (
                  <Chip
                    key={i}
                    label={name || '?'}
                    size="small"
                    sx={{
                      bgcolor: `${PURPLE}18`,
                      color: PURPLE,
                      fontWeight: 500,
                      height: 22,
                      fontSize: 11,
                      '& .MuiChip-label': { px: 1 },
                    }}
                  />
                );
              })}
            </Box>
          );
        },
      },
      {
        field: 'shot_description',
        headerName: 'Description',
        flex: 2,
        minWidth: 160,
        sortable: true,
        editable: false,
        renderCell: (params) => <TruncatedCell value={params.value} />,
      },
      {
        field: 'notes',
        headerName: 'Notes',
        flex: 1,
        minWidth: 120,
        sortable: true,
        editable: false,
        renderCell: (params) => <TruncatedCell value={params.value} />,
      },
    ];

    const prefix = [...(dragCol ? [dragCol] : []), ...(actionCol ? [actionCol] : [])];

    if (groupByScene) return [...prefix, ...shotCols];

    return [
      ...prefix,
      {
        field: 'sceneNumber',
        headerName: 'Scene #',
        width: 72,
        minWidth: 60,
        sortable: true,
        editable: false,
        renderCell: (params) => <ShotNumberBadge label={params.value} circle />,
      },
      {
        field: 'sceneIntExt',
        headerName: 'INT/EXT',
        width: 90,
        minWidth: 70,
        sortable: true,
        editable: false,
        renderCell: (params) => <TruncatedCell value={params.value} />,
      },
      {
        field: 'sceneHeader',
        headerName: 'Location',
        flex: 1,
        minWidth: 120,
        sortable: true,
        editable: false,
        renderCell: (params) => <TruncatedCell value={params.value} />,
      },
      {
        field: 'sceneTimeOfDay',
        headerName: 'Time',
        width: 110,
        minWidth: 80,
        sortable: true,
        editable: false,
        renderCell: (params) => <TruncatedCell value={params.value} />,
      },
      {
        field: 'pageLength',
        headerName: 'Pages',
        width: 68,
        minWidth: 55,
        sortable: true,
        editable: false,
        align: 'left',
        headerAlign: 'left',
        valueGetter: (v) => v ?? '',
        renderCell: (params) => toEighths(params.value),
      },
      ...shotCols,
    ];
  }, [
    dictionaries,
    dictsLoaded,
    subjectsValueGetter,
    resolveSubjectName,
    groupByScene,
    isReadOnly,
    handleAddShot,
    handleDeleteShot,
    density,
  ]);

  const getCellClassName = useCallback(
    (params) =>
      !isReadOnly && !params.row.isGroupHeader && EDITABLE_FIELDS.has(params.field)
        ? 'sk-editable-cell'
        : '',
    [isReadOnly],
  );

  return (
    <ThemeProvider theme={MUItheme}>
      <RowDragContext.Provider value={isReadOnly ? null : dragCtx}>
        <Box
          sx={{
            height: 'calc(100vh - 130px)',
            minHeight: 400,
            width: '100%',
            px: { xs: 1, sm: 2 },
            pb: 2,
            pt: 1,
          }}
        >
          <DataGrid
            rows={rows}
            columns={columns}
            getRowId={(row) =>
              row.id != null && row.id !== 0
                ? row.id
                : `temp-${row.sceneId}-${row.shotNumber ?? 0}`
            }
            slots={{ toolbar: CustomTableToolbar, row: CustomGridRow }}
            slotProps={{
              toolbar: {
                quickFilterProps: { debounceMs: 200 },
                groupByScene,
                setGroupByScene,
                filterItems,
                onAddFilter: handleAddFilterItem,
                onRemoveFilter: handleRemoveFilterItem,
                onClearFilters: handleClearFilterItems,
                dictionaries,
              },
              columnsManagement: {
                getTogglableColumns: (cols) =>
                  cols
                    .filter((c) => c.field !== '__drag__' && c.field !== 'actions')
                    .map((c) => c.field),
              },
              panel: {
                placement: 'bottom-end',
              },
            }}
            sortModel={sortModel}
            onSortModelChange={setSortModel}
            filterModel={{ items: [], quickFilterValues }}
            onFilterModelChange={(m) => {
              setQuickFilterValues(m.quickFilterValues || []);
            }}
            sortingMode="server"
            filterMode="server"
            disableColumnFilter
            columnVisibilityModel={columnVisibilityModel}
            onColumnVisibilityModelChange={setColumnVisibilityModel}
            onCellClick={handleCellClick}
            getCellClassName={getCellClassName}
            getRowHeight={(params) => (params.model?.isGroupHeader ? 56 : null)}
            density={density}
            onDensityChange={setDensity}
            disableRowSelectionOnClick
            loading={!dictsLoaded}
            initialState={{
              pagination: { paginationModel: { pageSize: 50 } },
            }}
            pageSizeOptions={[25, 50, 100]}
            sx={{
              border: 'none',
              borderRadius: 2,
              backgroundColor: '#fff',
              boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
              '& .MuiDataGrid-columnHeaders': {
                backgroundColor: '#F9FAFC',
                fontSize: 12,
                fontWeight: 600,
                color: '#555',
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
              },
              '& .MuiDataGrid-columnHeader': { backgroundColor: '#F9FAFC' },
              '& .MuiDataGrid-cell': { fontSize: 13, display: 'flex', alignItems: 'center' },
              '& .MuiDataGrid-row:hover': { backgroundColor: 'rgba(122,100,235,0.03)' },
              '& .sk-editable-cell': {
                cursor: 'pointer',
                '&:hover': { backgroundColor: 'rgba(122,100,235,0.07)' },
              },
              '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': { outline: 'none' },
              '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within': {
                outline: 'none',
              },
              '& .MuiDataGrid-toolbarContainer': { px: 1, pb: 0, borderBottom: 'none' },
              '& .MuiDataGrid-footerContainer': { borderTop: '1px solid rgba(0,0,0,0.06)' },
              '& .MuiDataGrid-selectedRowCount': { display: 'none' },
              ...(density === 'comfortable' && {
                '& .MuiDataGrid-cell[data-field="image"]': { padding: '4px' },
              }),
            }}
          />
        </Box>
      </RowDragContext.Provider>

      <EditPopover
        open={popover.open}
        anchorEl={popover.anchorEl}
        field={popover.field}
        row={popover.row}
        dictionaries={dictionaries}
        onSave={handlePopoverSave}
        onCancel={handlePopoverCancel}
      />
    </ThemeProvider>
  );
};

export default ShotListTable;
